var loadJS = require('load-js');
require('url-search-params-polyfill');

customElements.define("google-maps", class extends HTMLElement {

    static get observedAttributes() { return ["key", "zoom", "latitude", "longitude"] }

    get key() { return this.getAttribute("key") }
    get locale() { return document.querySelector("html").getAttribute("lang") }
    get zoom() { return parseInt(this.getAttribute("zoom")) }
    get latitude() { return parseFloat(this.getAttribute("latitude")) }
    get longitude() { return parseFloat(this.getAttribute("longitude")) }
    get style() { return JSON.parse(this.querySelector("script[role=style]").textContent) }
    get ui() { return this.getAttribute("ui") == "on" }
    get scrollwheel() { return this.getAttribute("scrollwheel") == "on" }
    get clickzoom() { return this.getAttribute("clickzoom") == "off" }
    get cluster() { return this.querySelector("cluster-marker"); }

    get infoWindowTemplate() { return this.shadowRoot.getElementById("infowindow").content.cloneNode(true) }

    set latitude(value) { return this.setAttribute("latitude", parseFloat(value)) }
    set longitude(value) { return this.setAttribute("longitude", parseFloat(value)) }
    set zoom(value) { return this.setAttribute("zoom", parseInt(value)) }

    constructor() {

        super();

        const shadowRoot = this.attachShadow({ mode: 'open' });

        this.idle = true;
        this.clusterer = null;
        this.markers = [];
        this.infowindows = [];

        let style = document.createElement("style");

        style.textContent = `
            :host {
                display: block;
                width: 100%;
                height: 100%;
            }
            .map {
                width: 100%;
                height: 100%;
            }
        `;

        this.shadowRoot.appendChild(style);

    }

    connectedCallback() {

        this.mapElement = document.createElement("div");
        this.mapElement.classList.add("map");

        this.shadowRoot.appendChild(this.mapElement);

        this.init();

    }

    init() {

        if (!this.key) return;

        let process = () => {

            let options = {
                disableDefaultUI: !this.ui,
                scrollwheel: this.scrollwheel,
                doubleClickZoom: this.clickzoom,
                zoom: this.zoom || 14,
                styles: this.style || []
            };

            if (this.latitude && this.longitude) {

                options.center = {
                    lat: this.latitude,
                    lng: this.longitude
                };

            }

            this.map = new google.maps.Map(this.mapElement, options);

            this.map.addListener("center_changed", () => {

                this.idle = false;

                let center = this.map.getCenter();

                this.setAttribute("latitude", center.lat());
                this.setAttribute("longitude", center.lng());

            });

            this.map.addListener("zoom_changed", () => {

                this.idle = false;

                this.setAttribute("zoom", this.map.getZoom());

            });

            this.map.addListener("idle", () => {

                let center = this.map.getCenter();

                this.setAttribute("latitude", center.lat());
                this.setAttribute("longitude", center.lng());

                this.idle = true;

            });

            this.update();

        }

        if (typeof google === "undefined") {

            let params = {};

            if (this.key) params.key = this.key;
            if (this.locale) params.locale = this.locale;

            let searchParams = new URLSearchParams(params);
            let googleMapsSrc = `https://maps.googleapis.com/maps/api/js?${searchParams.toString()}`;

            loadJS([{
                async: true,
                url: "https://cdnjs.cloudflare.com/ajax/libs/markerclustererplus/2.1.4/markerclusterer.js"
            }, {
                async: true,
                url: googleMapsSrc
            }]).then(process).catch(function (error) {

                console.error("google-maps error:", error);

            });

        } else process();

    }

    update(filter) {

        this.markers.forEach((marker) => {

            marker.setMap(null);

        });

        this.markers = [];

        this.querySelectorAll("map-marker").forEach((data) => {

            if (filter && !data.matches(filter)) return;

            let image;

            if (data.src) {

                image = {
                    url: data.src,
                    anchor: new google.maps.Point(data.cx, data.cy),
                    size: new google.maps.Size(data.width, data.height),
                    origin: new google.maps.Point(0, 0),
                    scaledSize: new google.maps.Size(data.width, data.height)
                };

            }

            let params = {
                position: {
                    lat: data.latitude,
                    lng: data.longitude
                },
                draggable: false,
                map: this.map
            };

            if (image) {

                params.icon = image;

            }

            let marker = new google.maps.Marker(params);

            this.markers.push(marker);

            if (data.hasInfoWindow()) {

                let infowindow = new google.maps.InfoWindow();

                this.infowindows.push(infowindow);

                marker.addListener("click", () => {

                    this.infowindows.forEach((infowindow) => {

                        infowindow.close();

                    });

                    let template = document.createElement("template");

                    template.innerHTML = `<slot name="${data.slot}"></slot>`;

                    infowindow.setContent(template.content.cloneNode(true));

                    infowindow.open(this.map, marker);

                });

            } else if (data.external) {

                marker.addListener("click", () => {

                    window.location.href = data.external;

                });

            }

            if (this.clusterer) {

                this.clusterer.clearMarkers();
                this.clusterer.addMarkers(this.markers);

            } else if (this.cluster) {

                let styles = [];

                for (let i = 0; i < 5; i++) {

                    styles.push({
                        url: this.cluster.src,
                        width: this.cluster.width,
                        height: this.cluster.height
                    });

                }

                var options = {
                    gridSize: Math.max(this.cluster.width, this.cluster.height),
                    maxZoom: 15,
                    styles: styles
                };

                this.clusterer = new MarkerClusterer(this.map, this.markers, options);

            }

        });

    }

    fit() {

        if (this.markers.length > 1) {

            let bounds = new google.maps.LatLngBounds();

            this.markers.forEach((marker) => {

                if (marker.getVisible()) {

                    bounds.extend(marker.getPosition());

                }

            });

            this.map.fitBounds(bounds);

        } else if (this.markers.length) {

            this.setAttribute("latitude", this.markers[0].getPosition().lat());
            this.setAttribute("longitude", this.markers[0].getPosition().lng());

        }

    }

    attributeChangedCallback(name, old, value) {

        switch (name) {

            case "key":

                this.init();

                break;

            case "latitude":
            case "longtitude":

                if (!this.map) return;

                if (this.idle) {

                    let point = new google.maps.LatLng(this.latitude, this.longitude)

                    this.map.panTo(point);

                }

                break;

            case "zoom":

                if (!this.map) return;

                if (this.map.getZoom() != this.zoom) {

                    this.map.setZoom(this.zoom);

                }

                break;

        }

    }

});